import { useCallback, useEffect, useRef, useState } from 'react';
import type React from 'react';
import type { ContactList } from '@/modules/contact';
import type { ContactAction } from '@/modules/contact/reducer';
import type { MessageDraft } from '@/modules/message';
import type { SharingMode } from '@/modules/template';
import { TopicLogic } from '@/modules/topic/logic';
import type { Topic } from '@/modules/topic/types';
import { MePrFoService } from '@/services/meprfo';
import { isQuotaError } from '@/services/storage';
import { ShareWizardLogic } from './logic';
import {
  STORAGE_SCHEMA_VERSION,
  clearPersistedWizardState,
  loadPersistedWizardState,
  savePersistedWizardState,
  shareSessionSeed,
  type PersistedMessageDraft,
} from './session_storage';

export interface UseWizardSessionParams {
  topicId: string;
  /** Null until the topic fetch resolves; the send queue needs it to rehydrate. */
  topic: Topic | null;
  isTopicLoading: boolean;
  /** The live recipient selection, from ContactStateContext. */
  recipients: ContactList;
  /** Whether the address book is still loading, which gates the restore. */
  contactsLoading: boolean;
  /** Set once the wizard page's enter transition has finished. */
  viewSettled: boolean;
  dispatch: React.Dispatch<ContactAction>;
  presentToast: (options: {
    message: string;
    duration: number;
    color: string;
  }) => void;
  /**
   * Called when a session with an in-progress leg is restored. The sender has
   * already been past the interstitials to get there, so the wizard marks them
   * all shown rather than interrupting a send in flight to re-ask.
   */
  markAllInterstitialsShown: () => void;
}

export interface WizardSession {
  // ── the stored session ────────────────────────────────────────────────────
  aboutChangesSince: string | undefined;
  previousLegId: string | undefined;
  /** The resharer's connection to whoever sent them the leg (garden only). */
  connectionToPrevLeg: string;
  sharingRunId: string | undefined;
  inProgressLegId: string | null;
  sendQueue: MessageDraft[];
  sendQueueCursor: number;
  confirmedContactIds: Set<string>;
  excludeAwareContacts: boolean;
  updateSummary: string;

  // ── derived from it ───────────────────────────────────────────────────────
  /**
   * Which of the intro's two messages this session composes (#447). Read off
   * `previousLegId` — a session holding one is passing that leg on — so the
   * mode cannot drift from the leg it is about.
   */
  sharingMode: SharingMode;
  /**
   * Sharing a change list is what makes this an update: the head of that list
   * is both the signal and the state the flow needs.
   */
  isUpdateFlow: boolean;
  /** True until both the storage read and the topic fetch have completed. */
  isLoadingLegState: boolean;

  // ── writing to it ─────────────────────────────────────────────────────────
  setInProgressLegId: (legId: string | null) => void;
  setSendQueue: React.Dispatch<React.SetStateAction<MessageDraft[]>>;
  setSendQueueCursor: (cursor: number) => void;
  setConfirmedContactIds: React.Dispatch<React.SetStateAction<Set<string>>>;
  setUpdateSummary: (value: string) => void;
  setConnectionToPrevLeg: (value: string) => void;
  /**
   * Set the exclude-aware box. The session stores whatever it is set to, so a
   * sender who clears a box their entry point ticked keeps it clear across the
   * set-names detour and a mid-flow reload (#581).
   */
  setExcludeAwareContacts: (value: boolean) => void;
  /** Start this flow's sharing run, or adopt the one already stored (#455). */
  setSharingRunId: (legId: string | undefined) => void;
  /** The run id read synchronously, for callers that create and use it in one tick. */
  readSharingRunId: () => string | undefined;

  // ── ending it ─────────────────────────────────────────────────────────────
  /**
   * End the session everywhere it lives: drop the stored copy, clear the
   * in-progress leg, and end this flow's sharing run (#455) so a later "Start
   * the ball rolling" begins a fresh run rather than extending this one. The
   * run id is the reason this is one call rather than a clear of the store —
   * it also lives in React state and in a ref, and nothing re-reads storage
   * while the provider is mounted, which it stays after the last send.
   *
   * The send queue is deliberately not part of it. Emptying it is what puts
   * `FinalShareConfirmStep` into its completion screen, and doing it on a path
   * that navigates away instead would trip the router's snap-back effect
   * (`ShareWizardRouter.tsx`) into a navigation competing with the exit.
   */
  resetSession: () => void;
  /** Drop the stored session alone, leaving live state to the caller. */
  clearStoredSession: () => void;
  /**
   * Write the session to storage right now, rather than waiting for the save
   * effect. The SMS handoff needs this: Capacitor Preferences is async and
   * Android may suspend the webview before an effect's write completes.
   */
  persistNow: (overrides?: {
    inProgressLegId?: string;
    sendQueueCursor?: number;
  }) => Promise<void>;
}

/**
 * The share wizard's session: the state that outlives the React tree, and the
 * reading and writing of it.
 *
 * One trip through the wizard is a session. It has to survive the SMS handoff
 * (the OS foregrounds Messages and may suspend the webview mid-send), the
 * set-names detour to /profile, and a mid-flow reload — so every field here is
 * both live state and a stored field, and the two are kept in step by the four
 * effects below: load, restore recipients, rehydrate the send queue, save.
 *
 * The provider owns what is merely on screen; this owns what a reload must not
 * lose.
 */
export function useWizardSession({
  topicId,
  topic,
  isTopicLoading,
  recipients,
  contactsLoading,
  viewSettled,
  dispatch,
  presentToast,
  markAllInterstitialsShown,
}: UseWizardSessionParams): WizardSession {
  // aboutChangesSince — the head of the change list being shared — both signals
  // the update flow and drives the Changes step ("Since {timestamp}" + recipient
  // filtering). Its value comes entirely from persisted state, restored below:
  // bootstrapped (with the summary) when the user taps Share Updates / Reshare,
  // and also present for a sender returning from SMS. Persisted state is the
  // single carrier — no URL param. (#427)
  const [aboutChangesSince, setAboutChangesSince] = useState<
    string | undefined
  >(undefined);
  // Reshare mode (#447): the leg being passed on. Holding one IS the reshare
  // signal, the same way a persisted aboutChangesSince signals the update flow
  // — there is no second flag that could disagree with it.
  const [previousLegId, setPreviousLegId] = useState<string | undefined>(
    undefined
  );
  // The starting leg of the sharing run this flow started (#455), created just
  // before the flow enters its share-confirm steps. The ref mirrors the state
  // for synchronous reads inside the SMS handoff, which may create the starting
  // leg and reference it in one tick.
  const [sharingRunId, setSharingRunIdState] = useState<string | undefined>(
    undefined
  );
  const sharingRunIdRef = useRef<string | undefined>(undefined);
  const [inProgressLegId, setInProgressLegId] = useState<string | null>(null);
  const [sendQueue, setSendQueue] = useState<MessageDraft[]>([]);
  const [sendQueueCursor, setSendQueueCursor] = useState<number>(0);
  const [confirmedContactIds, setConfirmedContactIds] = useState<Set<string>>(
    new Set()
  );
  // The sender's ≤100-char summary of what changed, typed in the Changes step
  // and rendered by the {_updateSummary} primitive (#427).
  const [updateSummary, setUpdateSummary] = useState<string>('');
  // How a resharer knows whoever sent them the leg they pass on, asked once
  // per reshare by the garden composer's ConnectionField.
  const [connectionToPrevLeg, setConnectionToPrevLeg] = useState<string>('');
  // "Exclude recipients who've seen this topic before" (#581). The session
  // carries it: the entry point seeds it when the session starts — off with a
  // change list, else on for a reshare, else the topic type's opening position
  // (see shareSessionSeed) — and it stands until the sender changes the box or
  // the session ends.
  const [excludeAwareContacts, setExcludeAwareContacts] =
    useState<boolean>(false);
  const [isLoadingLegState, setIsLoadingLegState] = useState<boolean>(true);
  const [persistedStateLoaded, setPersistedStateLoaded] =
    useState<boolean>(false);

  // Passing a leg on is what makes this a reshare; there is nothing else to
  // read, so the mode cannot drift from the leg it is about.
  const sharingMode: SharingMode = previousLegId ? 'reshare' : 'fresh';
  // Sharing a change list is what makes this an update: the head of that list
  // is both the signal and the state the flow needs, so there is nothing else
  // to derive it from.
  const isUpdateFlow = aboutChangesSince != null;

  const setSharingRunId = useCallback((legId: string | undefined) => {
    sharingRunIdRef.current = legId;
    setSharingRunIdState(legId);
  }, []);
  const readSharingRunId = useCallback(() => sharingRunIdRef.current, []);
  const clearSharingRun = useCallback(
    () => setSharingRunId(undefined),
    [setSharingRunId]
  );

  const clearStoredSession = useCallback(
    () => clearPersistedWizardState(topicId),
    [topicId]
  );

  const resetSession = useCallback(() => {
    clearStoredSession();
    setInProgressLegId(null);
    clearSharingRun();
  }, [clearSharingRun, clearStoredSession]);

  // Persisted drafts from storage, held until the topic is fetched so we can
  // reconstruct full MessageDraft objects (with the topic reference).
  const pendingPersistedDrafts = useRef<PersistedMessageDraft[] | null>(null);

  // Set by the load effect when it finds no session at all — see the cold-start
  // effect below.
  const coldStart = useRef(false);

  // Pending pre-send recipient restore (#479). The load effect stashes the
  // persisted selection here; it is applied (SET_RECIPIENTS) only once BOTH of
  // these hold, or it gets clobbered / blanks the page:
  //   1. The address book has finished loading (`!contactsLoading`). On a cold
  //      load ContactStateProvider loads the book async and dispatches
  //      SET_CONTACTS, which resets recipientIds to [] — restoring before that
  //      lands is silently wiped, then the persist effect clears storage.
  //   2. The wizard page has settled. Restoring dispatches into
  //      ContactStateContext, which sits ABOVE the router outlet; doing so while
  //      the page is still transitioning in (the set-names detour return)
  //      re-renders the outlet and wedges the page blank (`ion-page-invisible`).
  //      A cold load has no such transition, so it needs only condition 1.
  const pendingRestoreRecipients = useRef<ContactList | null>(null);
  const pendingRestoreConfirmed = useRef<string[] | null>(null);
  // A cold load is distinguished from a warm remount (the detour return) by
  // whether the address book was ever loading under this provider: on a cold
  // load ContactStateProvider mounts alongside us with isLoading=true; on a warm
  // remount the book is already loaded, so isLoading is false throughout. Only a
  // warm remount has an enter transition to wait out (condition 2 above).
  const sawContactsLoadingRef = useRef<boolean>(false);
  if (contactsLoading) sawContactsLoadingRef.current = true;
  // True from when the load effect stashes a recipient restore until it is
  // applied. The persist effect MUST NOT run while this is set: `recipients` is
  // still empty in the restore window, so the discard branch would treat the
  // session as pristine and wipe the stored selection — which a reload in that
  // window then reads back as empty, permanently losing the recipients.
  const [recipientRestorePending, setRecipientRestorePending] =
    useState<boolean>(false);

  const applyRecipientRestore = useCallback(() => {
    const confirmed = pendingRestoreConfirmed.current;
    if (confirmed) {
      pendingRestoreConfirmed.current = null;
      setConfirmedContactIds(new Set(confirmed));
    }
    const recips = pendingRestoreRecipients.current;
    if (recips) {
      pendingRestoreRecipients.current = null;
      dispatch({ type: 'SET_RECIPIENTS', payload: recips });
    }
    setRecipientRestorePending(false);
  }, [dispatch]);

  // Apply the stashed pre-send recipient restore once its preconditions hold:
  // the address book is loaded, and — for a warm remount that animates in — the
  // enter transition has settled. A cold load (address book was loading) has no
  // transition, so it applies as soon as the book is ready.
  useEffect(() => {
    if (!recipientRestorePending) return;
    if (contactsLoading) return;
    const isColdLoad = sawContactsLoadingRef.current;
    if (!isColdLoad && !viewSettled) return;
    applyRecipientRestore();
  }, [
    recipientRestorePending,
    contactsLoading,
    viewSettled,
    applyRecipientRestore,
  ]);

  // ── load ────────────────────────────────────────────────────────────────
  useEffect(() => {
    loadPersistedWizardState(topicId).then(async persisted => {
      // Point sharingRunId at the run the stored wizard state names, or at
      // undefined when it names no run (#455). Unconditional because this
      // effect re-runs whenever topicId changes: an assignment made only when
      // a run was stored could never clear the id left behind by the topic
      // just left, so the next flow would reuse that run — its legs would
      // hang off that run's start, and its recipients would read that run's
      // allowed detail instead of the one in the message they were sent. When
      // a run IS stored, picking it up is what keeps the later sends of a
      // single flow, across the SMS handoff, in the run its first send
      // opened.
      setSharingRunId(persisted?.sharingRunId);
      if (persisted) {
        // Every field below except the recipient selection restores eagerly into
        // hook-local state: those updates re-render only inside the wizard
        // page and nothing external resets them, so they're always safe to apply
        // as soon as they're read. Only `recipients` restore into the shared
        // ContactStateContext (above the router outlet), which is why it alone is
        // deferred and gated (see pendingRestoreRecipients / the restore effect):
        // that context is reset by the address book's own SET_CONTACTS load and
        // wedges the page blank if mutated mid enter-transition.
        if (persisted.inProgressLegId) {
          // Validate the leg still exists on the server. Stale legs can
          // survive PWA reinstalls on Android because Chrome does not clear
          // site data when a WebAPK is uninstalled.
          const leg = await MePrFoService.getSharingLeg(
            persisted.inProgressLegId
          ).catch(() => true); // network error → assume valid, don't discard
          if (leg === null) {
            clearPersistedWizardState(topicId);
            setPersistedStateLoaded(true);
            return;
          }
          setInProgressLegId(persisted.inProgressLegId);
          markAllInterstitialsShown();
        }
        pendingPersistedDrafts.current = persisted.sendQueue;
        setSendQueueCursor(persisted.sendQueueCursor);
        // Restore the update flow (#427): the persisted aboutChangesSince is the
        // update-mode signal (bootstrapped on Share Updates / Reshare, and
        // present for a sender returning from SMS), and the persisted summary
        // prefills the Changes step.
        if (persisted.aboutChangesSince) {
          setAboutChangesSince(persisted.aboutChangesSince);
        }
        if (persisted.updateSummary) {
          setUpdateSummary(prev => prev || persisted.updateSummary || '');
        }
        // Restore the reshare mode (#447): the persisted leg being passed on
        // is the reshare signal, mirroring aboutChangesSince above.
        if (persisted.previousLegId) {
          setPreviousLegId(persisted.previousLegId);
        }
        if (persisted.connectionToPrevLeg) {
          setConnectionToPrevLeg(
            prev => prev || persisted.connectionToPrevLeg || ''
          );
        }
        // Stash the pre-send recipient selection (#479); the restore effect
        // above applies it once the address book is loaded and the page has
        // settled. The recipient box is otherwise in-memory only, so it was lost
        // whenever the ShareWizard outlet unmounted (the set-names detour to
        // /profile) or the page was reloaded mid-flow. confirmedContactIds rides
        // along so the compose steps still exclude anyone already sent to.
        if (persisted.confirmedContactIds?.length) {
          pendingRestoreConfirmed.current = persisted.confirmedContactIds;
        }
        // A stored `false` is as meaningful as a stored `true` — an update
        // seeds it off, and a sender may have cleared the box — so restore
        // either.
        if (typeof persisted.excludeAware === 'boolean') {
          setExcludeAwareContacts(persisted.excludeAware);
        }
        if (persisted.recipients?.length) {
          pendingRestoreRecipients.current = persisted.recipients;
          // Freeze the persist effect until the restore lands, so its discard
          // branch can't wipe the stored selection while `recipients` is still
          // empty in the restore window.
          setRecipientRestorePending(true);
        }
      }
      // No session at all means no entry point ran — see the cold-start effect.
      coldStart.current = !persisted;
      // Signal storage read is complete; the reconstruct effect will wait for
      // the topic fetch to also complete before setting isLoadingLegState false.
      setPersistedStateLoaded(true);
    });
    // markAllInterstitialsShown is deliberately not a dependency: re-reading
    // storage because a callback identity changed would re-run the whole
    // restore, including the leg-validation round trip.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [topicId, setSharingRunId]);

  // ── rehydrate ───────────────────────────────────────────────────────────
  // Once both the storage read and the topic fetch have completed, reconstruct
  // the in-memory sendQueue from the slimmed persisted drafts.
  useEffect(() => {
    if (!persistedStateLoaded || isTopicLoading) return;

    const drafts = pendingPersistedDrafts.current;
    if (drafts && topic) {
      setSendQueue(
        drafts.map(d => ({
          header: {
            topic: topic,
            intent: d.header.intent,
            type: d.header.type,
            recipients: d.header.recipients,
          },
          body: d.body,
        }))
      );
      pendingPersistedDrafts.current = null;
    }

    setIsLoadingLegState(false);
  }, [persistedStateLoaded, isTopicLoading, topic, topicId]);

  // ── cold start ──────────────────────────────────────────────────────────
  // The wizard reached by URL with no session behind it — a typed or
  // bookmarked /topics/{id}/share, or one opened after storage was cleared.
  // Every button that opens the wizard calls startShareSession first, so there
  // is normally one to read; when there is not, the URL is the entry point, and
  // the wizard opens on what the dock's own Share button would have started
  // opens on. It asks that same function rather than deciding here, so the
  // exclude-aware rule (#581) stays in one place — but takes only the answer:
  // the wizard owns storage from mount on, and its save effect has already
  // written a session carrying the prepopulated recipients (#436, #479) that a
  // second write from here would drop.
  //
  // Waits for the topic, because its type is what the answer depends on.
  useEffect(() => {
    if (!persistedStateLoaded || !topic || !coldStart.current) return;
    coldStart.current = false;
    const seed = shareSessionSeed({
      typeDefaultsToExcludingAware:
        TopicLogic.defaultsToExcludingAwareRecipients(topic.type),
    });
    setExcludeAwareContacts(seed.excludeAware ?? false);
  }, [persistedStateLoaded, topic, topicId]);

  // The whole session as it stands, which is what both the save effect and an
  // explicit persistNow write. Held in a ref so persistNow can be a stable
  // callback the send machine depends on without re-creating on every keystroke.
  const liveSessionRef = useRef({
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    updateSummary,
    aboutChangesSince,
    previousLegId,
    connectionToPrevLeg,
    sharingRunId,
    recipients,
    confirmedContactIds,
    excludeAwareContacts,
  });
  liveSessionRef.current = {
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    updateSummary,
    aboutChangesSince,
    previousLegId,
    connectionToPrevLeg,
    sharingRunId,
    recipients,
    confirmedContactIds,
    excludeAwareContacts,
  };

  const persistNow = useCallback(
    async (overrides?: {
      inProgressLegId?: string;
      sendQueueCursor?: number;
    }) => {
      const live = liveSessionRef.current;
      await savePersistedWizardState(topicId, {
        schemaVersion: STORAGE_SCHEMA_VERSION,
        sendQueue: live.sendQueue,
        sendQueueCursor: overrides?.sendQueueCursor ?? live.sendQueueCursor,
        inProgressLegId:
          overrides?.inProgressLegId ?? live.inProgressLegId ?? undefined,
        updateSummary: live.updateSummary || undefined,
        aboutChangesSince: live.aboutChangesSince,
        previousLegId: live.previousLegId,
        connectionToPrevLeg: live.connectionToPrevLeg || undefined,
        // Read through the ref rather than the state: the handoff may create
        // the run and persist it in the same tick.
        sharingRunId: sharingRunIdRef.current,
        recipients: live.recipients,
        confirmedContactIds: Array.from(live.confirmedContactIds),
        excludeAware: live.excludeAwareContacts,
      });
    },
    [topicId]
  );

  // ── save ────────────────────────────────────────────────────────────────
  useEffect(() => {
    if (isLoadingLegState) return;
    // Hold off while a deferred recipient restore is outstanding — otherwise the
    // discard branch below wipes the stored selection during the restore window.
    if (recipientRestorePending) return;
    if (
      ShareWizardLogic.shouldDiscardPersistedState({
        inProgressLegId,
        sendQueueLength: sendQueue.length,
        previousLegId,
        aboutChangesSince,
        recipientCount: recipients.length,
        excludeAwareContacts,
      })
    ) {
      clearPersistedWizardState(topicId);
      return;
    }
    savePersistedWizardState(topicId, {
      schemaVersion: STORAGE_SCHEMA_VERSION,
      sendQueue,
      sendQueueCursor,
      inProgressLegId: inProgressLegId ?? undefined,
      updateSummary: updateSummary || undefined,
      aboutChangesSince,
      previousLegId,
      connectionToPrevLeg: connectionToPrevLeg || undefined,
      sharingRunId,
      recipients,
      confirmedContactIds: Array.from(confirmedContactIds),
      // Stored as a plain boolean rather than only when true: `false` is what
      // distinguishes a sender who cleared the box from one who never saw it,
      // and on a news topic those restore differently.
      excludeAware: excludeAwareContacts,
    }).catch(error => {
      if (isQuotaError(error)) {
        presentToast({
          message:
            'Device storage is full. Your sending progress may not be saved.',
          duration: 8000,
          color: 'danger',
        });
      }
    });
  }, [
    topicId,
    aboutChangesSince,
    sendQueue,
    sendQueueCursor,
    inProgressLegId,
    isLoadingLegState,
    previousLegId,
    connectionToPrevLeg,
    sharingRunId,
    presentToast,
    updateSummary,
    recipients,
    confirmedContactIds,
    recipientRestorePending,
    excludeAwareContacts,
  ]);

  return {
    aboutChangesSince,
    previousLegId,
    connectionToPrevLeg,
    sharingRunId,
    inProgressLegId,
    sendQueue,
    sendQueueCursor,
    confirmedContactIds,
    excludeAwareContacts,
    updateSummary,

    sharingMode,
    isUpdateFlow,
    isLoadingLegState,

    setInProgressLegId,
    setSendQueue,
    setSendQueueCursor,
    setConfirmedContactIds,
    setUpdateSummary,
    setConnectionToPrevLeg,
    setExcludeAwareContacts,
    setSharingRunId,
    readSharingRunId,

    resetSession,
    clearStoredSession,
    persistNow,
  };
}
